import { type DetailedHTMLProps, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { type TextColorType, type TextVariantType, type TextWeightType } from '../types';

type TextHTMLType = DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>;

export const textVariantClasses: Record<TextVariantType, string> = {
  kpi: cn('text-2xl leading-8 tracking-[-0.5px]'),
  headline: cn('text-xl leading-6 tracking-[-0.5px]'),
  subhead: cn('text-base leading-5 tracking-[-0.03em]'),
  label: cn('text-base leading-5 tracking-[-0.03em]'),
  body1: cn('text-base leading-5 tracking-[-0.5px]'),
  body2: cn('text-sm leading-5 tracking-[-0.5px]'),
  footnote: cn('text-sm leading-5 tracking-[-0.03em]'),
  caption1: cn('text-xs leading-4 tracking-[-0.41px]'),
  caption2: cn('text-xxs leading-3 tracking-[-0.41px]'),
};

const colorClasses: Record<TextColorType, string> = {
  black: cn('text-gray-900'),
  green: cn('text-green-500'),
  red: cn('text-red-400'),
  white: cn('text-white'),
  grey: cn('text-gray-600'),
  'light-green': cn('text-primary'),
  'dark-grey': cn('text-gray-900'),
  'dark-green': cn('text-green-700'),
};

const weightClasses: Record<TextWeightType, string> = {
  'extra-light': cn('font-extralight'),
  light: cn('font-light'),
  regular: cn('font-normal'),
  medium: cn('font-medium'),
  'semi-bold': cn('font-semibold'),
  bold: cn('font-bold'),
  'extra-bold': cn('font-extrabold'),
  'heavy-bold': cn('font-black'),
};

export type TextProps = Omit<TextHTMLType, 'ref'> & {
  variant: TextVariantType;
  color?: TextColorType;
  weight?: TextWeightType;
  italic?: boolean;
  underline?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  pointer?: boolean;
  value?: ReactNode;
  disabled?: boolean;
};

export const getDefaultTextWeight = (variant: TextVariantType): TextWeightType => {
  switch (variant) {
    case 'subhead':
      return 'bold';
    case 'kpi':
    case 'label':
    case 'footnote':
      return 'semi-bold';
    case 'headline':
    case 'body1':
    case 'body2':
    case 'caption1':
    case 'caption2':
      return 'regular';
  }
};

const Text = ({
  className,
  variant,
  color = 'black',
  weight,
  italic,
  underline,
  uppercase,
  lowercase,
  pointer,
  disabled,
  value,
  children,
  ...rest
}: TextProps) => {
  const resolvedWeight = weight ?? getDefaultTextWeight(variant);

  return (
    <span
      {...rest}
      className={cn(
        'font-sans',
        textVariantClasses[variant],
        colorClasses[color],
        weightClasses[resolvedWeight],
        italic && 'italic',
        underline && 'underline',
        uppercase && 'uppercase',
        lowercase && 'lowercase',
        pointer && 'cursor-pointer',
        disabled && 'cursor-not-allowed text-gray-500',
        className,
      )}
    >
      {children ?? value}
    </span>
  );
};

export default Text;
